import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f0faf4 0%, #e6f7ed 100%);
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Breadcrumbs = styled.nav`
  margin-bottom: 30px;
  font-size: 14px;
  color: #666;

  a {
    color: #1a9d4a;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  span {
    margin: 0 8px;
    color: #999;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const AppBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #2bee79;
  color: #0a3d1f;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1a1a1a;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const TableOfContents = styled.div`
  background: #f5fdf8;
  border-left: 4px solid #2bee79;
  padding: 20px 25px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 30px;

  h3 {
    color: #1a1a1a;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }

  ol {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: #1a9d4a;
    text-decoration: none;
    font-size: 15px;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Section = styled.section`
  margin-bottom: 35px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: #1a1a1a;
  font-size: 1.4rem;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e6f7ed;
  display: flex;
  align-items: center;
  gap: 10px;

  .icon {
    color: #1a9d4a;
  }
`;

const Paragraph = styled.p`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  font-size: 15px;
`;

const List = styled.ul`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  padding-left: 25px;

  li {
    margin-bottom: 10px;
    font-size: 15px;
  }
`;

const DataTable = styled.div`
  overflow-x: auto;
  margin: 20px 0;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th,
    td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid #e6f7ed;
    }

    th {
      background: #f0faf4;
      color: #1a1a1a;
      font-weight: 600;
    }

    td {
      color: #444;
    }

    tr:hover td {
      background: #f5fdf8;
    }
  }
`;

const HighlightBox = styled.div`
  background: #f0faf4;
  border-left: 4px solid #2bee79;
  padding: 15px 20px;
  border-radius: 0 8px 8px 0;
  margin: 20px 0;

  p {
    margin: 0;
    color: #444;
    font-size: 14px;
  }

  strong {
    color: #1a1a1a;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #1a9d4a 0%, #127a38 100%);
  color: white;
  padding: 25px;
  border-radius: 12px;
  text-align: center;

  h3 {
    margin-bottom: 15px;
    font-size: 1.2rem;
  }

  p {
    margin-bottom: 10px;
    font-size: 15px;
  }

  a {
    color: white;
    font-weight: 600;
  }
`;

const RelatedLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #e6f7ed;
    color: #1a9d4a;
    padding: 12px 20px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #1a9d4a;
      color: white;
    }
  }
`;

export const TipShiftPrivacyPage = () => {
  return (
    <>
      <Navbar />
      <PageContainer>
        <ContentWrapper>
          <Breadcrumbs>
            <Link to="/">Home</Link>
            <span>&rsaquo;</span>
            <span>Privacy Policy - TipShift</span>
          </Breadcrumbs>

          <Header>
            <AppBadge>TipShift</AppBadge>
            <Title>Privacy Policy</Title>
            <LastUpdated>Last updated: March 2026</LastUpdated>
          </Header>

          <Card>
            <TableOfContents>
              <h3>Table of Contents</h3>
              <ol>
                <li>
                  <a href="#introduction">Introduction</a>
                </li>
                <li>
                  <a href="#information-collected">Information We Collect</a>
                </li>
                <li>
                  <a href="#how-we-use">How We Use Your Information</a>
                </li>
                <li>
                  <a href="#third-party">Third-Party Services</a>
                </li>
                <li>
                  <a href="#data-storage">Data Storage and Security</a>
                </li>
                <li>
                  <a href="#data-retention">Data Retention</a>
                </li>
                <li>
                  <a href="#your-rights">Your Rights</a>
                </li>
                <li>
                  <a href="#children">Children's Privacy</a>
                </li>
                <li>
                  <a href="#changes">Changes to This Policy</a>
                </li>
                <li>
                  <a href="#contact">Contact</a>
                </li>
              </ol>
            </TableOfContents>

            <Section id="introduction">
              <SectionTitle>
                <span className="icon">1.</span> Introduction
              </SectionTitle>
              <Paragraph>
                TipShift ("the App") is operated by{" "}
                <strong>Monaco IT Services LLC</strong>, a Delaware limited
                liability company ("we", "us", "our"). This Privacy Policy
                describes how we collect, use, store, and protect your
                information when you use TipShift, available for iOS and
                Android.
              </Paragraph>
              <Paragraph>
                By downloading, installing, or using TipShift, you agree to the
                practices described in this Privacy Policy. If you do not agree
                with these practices, please do not use our application.
              </Paragraph>
              <HighlightBox>
                <p>
                  <strong>Our commitment:</strong> We are committed to protecting
                  your privacy and being transparent about how we handle your
                  data. We only collect the information necessary to provide and
                  improve our services.
                </p>
              </HighlightBox>
            </Section>

            <Section id="information-collected">
              <SectionTitle>
                <span className="icon">2.</span> Information We Collect
              </SectionTitle>
              <Paragraph>
                We collect different types of information to provide and improve
                our service:
              </Paragraph>
              <DataTable>
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Specific Data</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Device Identifier</strong>
                      </td>
                      <td>Unique UUID generated on your device</td>
                      <td>Authentication and data syncing</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Tip Data</strong>
                      </td>
                      <td>
                        Amounts, dates, receipt methods, hours worked, payout
                        status
                      </td>
                      <td>Core app functionality and reporting</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Email (optional)</strong>
                      </td>
                      <td>Email address if provided</td>
                      <td>Account recovery</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Analytics Events</strong>
                      </td>
                      <td>Anonymous usage events (screen views, feature usage)</td>
                      <td>Improving the app experience</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Push Tokens</strong>
                      </td>
                      <td>Device push notification token</td>
                      <td>Daily reminder notifications</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Subscription Status</strong>
                      </td>
                      <td>Active Pro subscription and expiration date</td>
                      <td>Managing subscription features</td>
                    </tr>
                  </tbody>
                </table>
              </DataTable>
            </Section>

            <Section id="how-we-use">
              <SectionTitle>
                <span className="icon">3.</span> How We Use Your Information
              </SectionTitle>
              <List>
                <li>
                  <strong>Provide the service:</strong> To maintain your tip
                  records, calculate earnings, and generate reports.
                </li>
                <li>
                  <strong>Sync data:</strong> To sync your tip data across
                  sessions and devices securely.
                </li>
                <li>
                  <strong>Reminders:</strong> To send daily reminder
                  notifications if you opt in.
                </li>
                <li>
                  <strong>Reports:</strong> To generate tax-related reports
                  (Form 4070-A, employer summaries, CSV/PDF exports).
                </li>
                <li>
                  <strong>Subscriptions:</strong> To process and manage your
                  TipShift Pro subscription.
                </li>
                <li>
                  <strong>Improvements:</strong> To analyze anonymous usage
                  patterns and improve the app.
                </li>
              </List>
            </Section>

            <Section id="third-party">
              <SectionTitle>
                <span className="icon">4.</span> Third-Party Services
              </SectionTitle>
              <Paragraph>
                We use the following third-party services that may receive
                limited data:
              </Paragraph>
              <DataTable>
                <table>
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Purpose</th>
                      <th>Data Shared</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>RevenueCat</strong>
                      </td>
                      <td>Subscription management</td>
                      <td>Device ID and purchase information</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Expo (EAS)</strong>
                      </td>
                      <td>App updates and push notifications</td>
                      <td>Device push token</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Sentry</strong>
                      </td>
                      <td>Error tracking and crash reports</td>
                      <td>Anonymous error data (no personal information)</td>
                    </tr>
                  </tbody>
                </table>
              </DataTable>
              <Paragraph>
                We do not sell or rent your personal information to third
                parties. Each provider is contractually obligated to protect your
                data and use it only for the specified purposes.
              </Paragraph>
            </Section>

            <Section id="data-storage">
              <SectionTitle>
                <span className="icon">5.</span> Data Storage and Security
              </SectionTitle>
              <List>
                <li>
                  <strong>Database:</strong> Your data is stored in a PostgreSQL
                  database hosted on Railway in the United States.
                </li>
                <li>
                  <strong>Offline-first:</strong> Tip data is stored locally on
                  your device and synced to our servers when connected.
                </li>
                <li>
                  <strong>Secure tokens:</strong> Authentication tokens are
                  stored in your device's secure keychain (SecureStore).
                </li>
                <li>
                  <strong>Encryption:</strong> All data in transit is encrypted
                  via HTTPS/TLS.
                </li>
                <li>
                  <strong>Access control:</strong> Only authorized personnel
                  have access to user data.
                </li>
              </List>
            </Section>

            <Section id="data-retention">
              <SectionTitle>
                <span className="icon">6.</span> Data Retention
              </SectionTitle>
              <List>
                <li>
                  <strong>Tip data:</strong> Retained until you delete it or
                  delete your account.
                </li>
                <li>
                  <strong>Analytics events:</strong> Automatically deleted after
                  90 days.
                </li>
                <li>
                  <strong>Backups:</strong> Maximum 5 retained per user; older
                  backups are automatically deleted.
                </li>
                <li>
                  <strong>Account deletion:</strong> All data is permanently
                  deleted via cascade when you delete your account through the
                  App.
                </li>
              </List>
              <HighlightBox>
                <p>
                  <strong>Important:</strong> Account deletion is permanent. All
                  your data including tip history, reports, and preferences will
                  be permanently removed and cannot be recovered.
                </p>
              </HighlightBox>
            </Section>

            <Section id="your-rights">
              <SectionTitle>
                <span className="icon">7.</span> Your Rights
              </SectionTitle>
              <List>
                <li>
                  <strong>Delete local data:</strong> You can delete all your
                  local data from Settings &gt; Clear All Data.
                </li>
                <li>
                  <strong>Delete account:</strong> You can permanently delete
                  your account and all server data from Settings &gt; Delete
                  Account.
                </li>
                <li>
                  <strong>Export data:</strong> You can export your data as CSV
                  or PDF at any time (Pro feature).
                </li>
                <li>
                  <strong>Contact us:</strong> You can request access to,
                  correction of, or deletion of your personal data by emailing
                  us.
                </li>
              </List>
            </Section>

            <Section id="children">
              <SectionTitle>
                <span className="icon">8.</span> Children's Privacy
              </SectionTitle>
              <Paragraph>
                TipShift is not intended for use by anyone under the age of 13.
                We do not knowingly collect personal information from children
                under 13. If we discover that we have collected data from a
                child under 13, we will delete it promptly.
              </Paragraph>
            </Section>

            <Section id="changes">
              <SectionTitle>
                <span className="icon">9.</span> Changes to This Policy
              </SectionTitle>
              <Paragraph>
                We may update this Privacy Policy from time to time. We will
                notify you of significant changes through the App or by updating
                the "Last updated" date on this page.
              </Paragraph>
            </Section>

            <Section id="contact">
              <SectionTitle>
                <span className="icon">10.</span> Contact
              </SectionTitle>
              <Paragraph>
                If you have questions about this Privacy Policy or how we handle
                your data:
              </Paragraph>
              <ContactCard>
                <h3>Monaco IT Services LLC</h3>
                <p>
                  Email:{" "}
                  <a href="mailto:support@tipshift.app">support@tipshift.app</a>
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://monaco-it-services.cl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    monaco-it-services.cl
                  </a>
                </p>
                <p>Jurisdiction: State of Delaware, United States</p>
              </ContactCard>
            </Section>

            <RelatedLinks>
              <Link to="/tipshift/support">Support Center</Link>
              <Link to="/">Back to Home</Link>
            </RelatedLinks>
          </Card>
        </ContentWrapper>
      </PageContainer>
      <Footer />
    </>
  );
};
